import React, { ChangeEvent, Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import { Backdrop, Box, Button, CircularProgress, Grid2, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSearchParams } from 'react-router-dom';
import { RootState } from '../../redux/reducers';
import { importAllGoods } from '../../redux/actions/goodsActions';
import { importClients } from '../../redux/actions/clientActions';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import GoodsTable from './GoodsTable';
import { GoodsStatus } from '../../api/ClientApi';
import { TableDetailsType } from './GoodsHelper';
import { DetailsContainerBox, DetailsTableContainerBox } from '../../common/ResponsiveStyle';
import { importApprovedOrderGoodsItems } from '../../redux/actions/orderGoodsItemsActions';
import { getSuppliers } from '../../redux/selectors/clientsSelector';
import { canSeeValuableInfo } from '../../redux/selectors/employeeSelector';
import ClientAutocomplete from '../../common/components/autocomplete/ClientAutocomplete';
import { formatNumber } from '../../common/Extensions';
import { FormatDateText } from '../../common/datesHelper';
import InvoicesTable from './InvoicesTable';
import { InvoiceSummary, buildInvoiceSummaries, getGoodsForInvoice } from './InvoiceHelper';

export default function GoodsByInvoiceList(): ReactElement {
    const goods = useAppSelector((state: RootState) => state.goods.goods);
    const approvedOrderGoodsItems = useAppSelector((state: RootState) => state.orderGoodsItems.approvedOrderGoodsItems);
    const suppliers = useAppSelector(getSuppliers);
    const clients = useAppSelector((state: RootState) => state.clients.clients);
    const canSeeMoney = useAppSelector(canSeeValuableInfo);
    const currency = useAppSelector((state: RootState) => state.companyDetails.companyDetails?.currency);

    // Dobavljač i otvorena faktura su u URL-u: browser "nazad" vraća sa fakture na spisak faktura.
    const [searchParams, setSearchParams] = useSearchParams();
    const supplierId = Number(searchParams.get('supplierId')) || undefined;
    const selectedInvoice = searchParams.get('invoice') ?? undefined;

    const [invoiceFilter, setInvoiceFilter] = useState<string>('');
    const [articleFilter, setArticleFilter] = useState<string>('');

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!goods) {
            dispatch(importAllGoods());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!approvedOrderGoodsItems) {
            dispatch(importApprovedOrderGoodsItems());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!clients) {
            dispatch(importClients());
        }
    }, [dispatch]);

    const invoices = useMemo(
        () => (goods ? buildInvoiceSummaries(goods, supplierId, invoiceFilter, articleFilter) : []),
        [goods, supplierId, invoiceFilter, articleFilter],
    );

    const invoiceGoods = useMemo(
        () => (goods && selectedInvoice ? getGoodsForInvoice(goods, supplierId, selectedInvoice) : []),
        [goods, supplierId, selectedInvoice],
    );

    const invoiceSummary = useMemo(
        () => (selectedInvoice ? buildInvoiceSummaries(invoiceGoods, supplierId, '', '')[0] : undefined),
        [invoiceGoods, supplierId, selectedInvoice],
    );

    const invoicesTotalValue = useMemo(() => invoices.reduce((acc, i) => acc + i.totalValue, 0), [invoices]);

    const formatMoney = (value: number) =>
        formatNumber(Math.round(value * 100) / 100) + (currency ? ' ' + currency : '');

    const handleSupplierChange = (id?: number) => {
        setSearchParams(id ? { supplierId: id.toString() } : {});
    };

    const handleSelectInvoice = (invoice: InvoiceSummary) => {
        const params: Record<string, string> = { invoice: invoice.invoiceNumber };
        const invoiceSupplierId = supplierId ?? invoice.supplierId;
        if (invoiceSupplierId) {
            params.supplierId = invoiceSupplierId.toString();
        }
        setSearchParams(params);
    };

    const handleBackToInvoices = () => {
        setSearchParams(supplierId ? { supplierId: supplierId.toString() } : {});
    };

    const handleInvoiceChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInvoiceFilter(e.target.value);
    };

    const handleArticleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setArticleFilter(e.target.value);
    };

    const invoiceInfo = invoiceSummary
        ? [
              invoiceSummary.supplierName,
              invoiceSummary.arrivalDate ? 'primljeno ' + FormatDateText(invoiceSummary.arrivalDate) : undefined,
              invoiceSummary.articlesCount + ' art.',
              canSeeMoney && invoiceSummary.totalValue > 0 ? formatMoney(invoiceSummary.totalValue) : undefined,
          ]
              .filter((v) => !!v)
              .join(' · ')
        : '';

    return goods === null ? (
        <Fragment>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={!Boolean(goods)}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Fragment>
    ) : (
        <DetailsContainerBox
            sx={{
                p: 2,
                backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'white' : theme.palette.grey[900]),
            }}
        >
            <Grid2 container sx={{ py: 2, px: { xs: 2, sm: 4 }, justifyContent: 'space-between', gap: 2 }}>
                <Grid2>
                    <Typography variant="h5">Roba po fakturi</Typography>
                </Grid2>
            </Grid2>
            <hr />
            <Box sx={{ p: { xs: 1, sm: 4 }, pt: 2 }}>
                <Grid2 container sx={{ display: 'flex', gap: 2, flexDirection: 'start', alignItems: 'center' }}>
                    <Box sx={{ width: { xs: '100%', sm: '300px' } }}>
                        <ClientAutocomplete
                            title="Dobavljač"
                            clients={suppliers ?? []}
                            selectedClientId={supplierId}
                            selectClient={handleSupplierChange}
                        />
                    </Box>
                    {!selectedInvoice && (
                        <TextField
                            size="small"
                            sx={{ width: { xs: '100%', sm: '200px' } }}
                            placeholder="Br. fakture"
                            value={invoiceFilter}
                            onChange={handleInvoiceChange}
                        />
                    )}
                    <TextField
                        size="small"
                        sx={{ width: { xs: '100%', sm: '200px' } }}
                        placeholder="Artikal"
                        value={articleFilter}
                        onChange={handleArticleChange}
                    />
                </Grid2>
                <Box sx={{ p: 0, mt: 2 }} />
                {selectedInvoice ? (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                            <Button size="small" startIcon={<ArrowBackIcon />} onClick={handleBackToInvoices}>
                                Sve fakture
                            </Button>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h6" sx={{ lineHeight: 1.3, wordBreak: 'break-word' }}>
                                    Faktura {selectedInvoice}
                                </Typography>
                                {invoiceInfo && (
                                    <Typography variant="body2" color="text.secondary">
                                        {invoiceInfo}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <DetailsTableContainerBox>
                            {invoiceGoods.length === 0 ? (
                                <Typography sx={{ p: 2, color: 'text.secondary' }}>
                                    Nema robe za ovu fakturu.
                                </Typography>
                            ) : (
                                <GoodsTable
                                    isSiteTable={false}
                                    isSiteWithClient={false}
                                    tableDetailsType={TableDetailsType.Arrival}
                                    goods={invoiceGoods}
                                    approvedOrderGoodsItems={approvedOrderGoodsItems}
                                    articleFilter={articleFilter.trim()}
                                    labelsFilter={''}
                                    goodsStatusFilter={GoodsStatus.InStock}
                                    batchSuppliersFilter={''}
                                    siteNameFilter={''}
                                    siteTypeFilter={null}
                                    uniqueBatchIdFilter={''}
                                    disableStatusFilter
                                />
                            )}
                        </DetailsTableContainerBox>
                    </>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Broj faktura: {invoices.length}
                            {canSeeMoney && invoicesTotalValue > 0
                                ? ' · ukupno ' + formatMoney(invoicesTotalValue)
                                : ''}
                        </Typography>
                        <DetailsTableContainerBox>
                            <InvoicesTable
                                invoices={invoices}
                                showSupplier={!supplierId}
                                onSelectInvoice={handleSelectInvoice}
                            />
                        </DetailsTableContainerBox>
                    </>
                )}
            </Box>
        </DetailsContainerBox>
    );
}
